import React from 'react';
import { Button, Input, Toaster } from '../common';
import { useForm } from 'react-hook-form';
import { useUpdatePassword } from '../../hooks/useProfile.ts';
import { useAuth } from '../../contexts/AuthContext';
import { getPasswordStrength } from '../../utils/general.ts';
import { passwordErrorMessages } from '../../lib/constants.tsx';
import { MdDone } from 'react-icons/md';
import toast from 'react-hot-toast';
import { FloppyDisk } from 'iconoir-react';

interface UpdatePasswordFormData {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const UpdatePasswordForm = () => {
  const { mutateAsync: updatePassword, isPending } = useUpdatePassword();

  const {
    register,
    handleSubmit,
    watch,
    setError,
    clearErrors,
    reset,
    formState: { errors },
  } = useForm<UpdatePasswordFormData>({
    defaultValues: {
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const { user } = useAuth();

  const onSubmit = async (data: UpdatePasswordFormData) => {
    if (data.newPassword !== data.confirmPassword) {
      toast.custom(t => (
        <Toaster
          message='Passwords do not match, Please try again.'
          type='error'
          id={t.id}
        />
      ));
      setError('newPassword', { message: '' });
      setError('confirmPassword', { message: '' });
      return;
    }

    const payload = {
      current_password: data.oldPassword,
      new_password: data.newPassword,
    };

    await updatePassword(payload, {
      onSuccess: () => {
        toast.custom(t => (
          <Toaster
            message='Password has been updated successfully'
            type='success'
            id={t.id}
          />
        ));
        reset();
      },
      onError: error => {
        toast.custom(t => (
          <Toaster message={error?.message as string} type='error' id={t.id} />
        ));
      },
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Input
        wrapperClassName='mb-6'
        type='password'
        label='Old Password'
        placeholder='Old password'
        {...register('oldPassword', {
          required: 'Old password is required',
        })}
        helperText={errors.oldPassword?.message as string}
        color={errors.oldPassword ? 'error' : 'default'}
      />
      <Input
        wrapperClassName='mb-6'
        type='password'
        label='New Password'
        placeholder='New password'
        {...register('newPassword', {
          required: 'New password is required',
          minLength: {
            value: 8,
            message: passwordErrorMessages.CHARACTERS,
          },
          pattern: {
            value: /^(?=.*[0-9])(?=.*[^A-Za-z0-9]).+$/,
            message: passwordErrorMessages.ALPHANUMERIC,
          },
          validate: value => {
            if (
              value
                .toLowerCase()
                .includes(user?.first_name?.toLowerCase() || '') ||
              value
                .toLowerCase()
                .includes(user?.last_name?.toLowerCase() || '') ||
              value === user?.email
            ) {
              return passwordErrorMessages.CONTAIN_NAME;
            }
            if (getPasswordStrength(value) === 'Weak') {
              return passwordErrorMessages.WEAK;
            }
          },
          onChange: () => {
            clearErrors('newPassword');
            clearErrors('confirmPassword');
          },
        })}
        color={errors.newPassword ? 'error' : 'default'}
      />
      {watch('newPassword') && (
        <div className='text-xs text-gray-500 mb-6'>
          <p
            className={`flex items-center gap-2 mb-2 font-semibold ${getPasswordStrength(watch('newPassword')) === 'Strong' ? 'text-green-600' : getPasswordStrength(watch('newPassword')) === 'Medium' ? 'text-yellow-600' : 'text-red-500'}`}
          >
            <MdDone size={16} /> Password strength:{' '}
            {getPasswordStrength(watch('newPassword'))}
          </p>
          <p
            className={`flex items-center gap-2 mb-2 font-semibold ${errors.newPassword?.message === passwordErrorMessages.CONTAIN_NAME ? 'text-red-500' : 'text-green-600'}`}
          >
            <MdDone size={16} /> Cannot contain your name or email address
          </p>
          <p
            className={`flex items-center gap-2 mb-2 font-semibold ${errors.newPassword?.message === passwordErrorMessages.CHARACTERS ? 'text-red-500' : 'text-green-600'}`}
          >
            <MdDone size={16} /> At least 8 characters
          </p>
          <p
            className={`flex items-center gap-2 mb-0 font-semibold ${errors.newPassword?.message === passwordErrorMessages.ALPHANUMERIC ? 'text-red-500' : 'text-green-600'}`}
          >
            <MdDone size={16} /> Contains a number or symbol
          </p>
        </div>
      )}
      <Input
        wrapperClassName='mb-6'
        type='password'
        label='Confirm Password'
        placeholder='Confirm password'
        {...register('confirmPassword', {
          required: 'Confirm password is required',
          onChange: () => {
            clearErrors('newPassword');
            clearErrors('confirmPassword');
          },
        })}
        helperText={errors.confirmPassword?.message as string}
        color={errors.confirmPassword ? 'error' : 'default'}
      />
      <div className='flex justify-end'>
        <Button
          className='px-[2.9688rem]'
          color='dark'
          type='submit'
          isLoading={isPending}
        >
          <FloppyDisk className='w-6 h-6' />
          Update
        </Button>
      </div>
    </form>
  );
};

export default UpdatePasswordForm;
